import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

import { Home } from './pages/Home';
import { SheetMusicScanner } from './pages/SheetMusicScanner';
import { Pricing } from './pages/Pricing';
import NavBar from './components/NavBar/NavBar';
import { OpenInGooglePlayBanner } from './components/OpenInGooglePlayBanner';
import { WorkspaceWithMsal } from './pages/Workspace';
import { ScanReviewWithMsal } from './pages/ScanReview';
import { ProfileWithMsal } from './pages/Profile';
import { LoadingSpinner } from './components/LoadingSpinner';
import { PdfProjectPageWithMsal } from './pages/PdfProjectPage';
import { SampleProjectPage } from './pages/SampleProjectPage';
import { Tutorial } from './pages/Tutorial';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { Support } from './pages/Support';
import { BusinessApiDocumentation } from './pages/BusinessApiDocumentation';
import { SignInPage } from './pages/SignIn';
import { SignUpPage } from './pages/SignUp';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { SSOCallback } from './pages/SSOCallback';
import { PostAuth } from './pages/PostAuth';
import { Logout } from './pages/Logout';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { initPostHog, reactPlugin } from './services/telemetry';
import { initUTMTracking } from './utils/utmTracking';

const ProjectPageWithMsal = lazy(() =>
  import('./pages/ProjectPage').then((module) => ({
    default: module.ProjectPageWithMsal,
  }))
);

const App = () => {
  // Initialize UTM tracking
  useEffect(() => {
    initUTMTracking();
    // Anonymous until NavBar/useAuth.ts identifies the signed-in user.
    initPostHog();
  }, []);

  return (
    <AppInsightsContext value={reactPlugin}>
      <OpenInGooglePlayBanner source="Global" />
      <NavBar />
      <Pages />
    </AppInsightsContext>
  );
};

function Pages() {
  return (
    <Routes>
      <Route path="/tutorial" element={<Tutorial />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/support" element={<Support />} />
      <Route path="/business-api" element={<BusinessApiDocumentation />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/sso-callback" element={<SSOCallback />} />
      <Route path="/post-auth" element={<PostAuth />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/workspace" element={<WorkspaceWithMsal />} />
      <Route path="/scan/review" element={<ScanReviewWithMsal />} />
      <Route path="/profile" element={<ProfileWithMsal />} />
      <Route
        path="/project/:projectId"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-[50vh] items-center justify-center">
                <LoadingSpinner variant="large" />
              </div>
            }
          >
            <ProjectPageWithMsal />
          </Suspense>
        }
      />
      <Route
        path="/pdf-project/:projectId"
        element={<PdfProjectPageWithMsal />}
      />
      {/* Public, no auth guard: sample scores are a landing surface. */}
      <Route path="/sample/:slug" element={<SampleProjectPage />} />
      <Route path="/sheet-music-scanner" element={<SheetMusicScanner />} />
      <Route path="/" element={<Home />} />
    </Routes>
  );
}

export default App;
